import { createContext } from 'react'
import type { EChartsOption, SeriesOption } from 'echarts'

export interface LineAppearance { width: number; points: boolean; pointSize: number }
export type ChartAppearance = Record<string, LineAppearance>
export const ChartDisplayContext = createContext<{ value?: ChartAppearance; onChange?: (value: ChartAppearance) => void; showControls?: boolean }>({})

/** Apply line appearance while preserving each chart's original hover behavior. */
export function presentChart(option: EChartsOption, appearance: ChartAppearance = {}): EChartsOption {
  const series = (Array.isArray(option.series) ? option.series : option.series ? [option.series] : []).map((series, index) => {
    const own = appearance[String(series.id ?? series.name ?? index)]
    return { ...series,
      ...(own && (series.type === 'line' || series.type === 'scatter') ? {
        ...(series.type === 'line' ? { lineStyle: { ...series.lineStyle, width: own.width }, showSymbol: own.points } : {}),
        symbol: 'circle', symbolSize: own.pointSize,
      } : {}),
    } as SeriesOption
  })
  return { ...option, series }
}

/** Whether two dependency lists are the same, item for item. */
export function sameDeps(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((item, i) => Object.is(item, b[i]))
}

type SavedZoom = Array<{ id?: string; start?: number; end?: number }> | undefined

/** Put back the zoom the reader had on the chart (by each zoom's id) when the
 *  chart is drawn again for the same data. A new option replaces the old one
 *  whole, which resets the zoom; that is right for new data and wrong when
 *  only the line appearance changed. */
export function keepZoom(option: EChartsOption, saved: SavedZoom): EChartsOption {
  if (!Array.isArray(option.dataZoom) || !saved?.length) return option
  return {
    ...option,
    dataZoom: option.dataZoom.map((zoom) => {
      const id = (zoom as { id?: string }).id
      const before = id ? saved.find((item) => item.id === id) : undefined
      return before ? { ...zoom, start: before.start, end: before.end } : zoom
    }),
  }
}
